import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ErrorBoundary } from '@/components/common';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout';
import { LoginPage, RegisterPage, ForgotPasswordPage } from '@/pages/auth';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { UsersPage, UserDetailPage } from '@/pages/users';
import { OAuthClientsPage } from '@/pages/oauth';
import { RolesPage } from '@/pages/rbac';
import { TenantsPage } from '@/pages/tenants';
import { PoliciesPage } from '@/pages/policies';
import { AnalyticsPage } from '@/pages/analytics';
import { SettingsPage } from '@/pages/settings';
import { ROUTES } from '@/config/api';
import { checkAndClearInvalidAuth } from '@/utils/authCheck';
import { Outlet } from 'react-router-dom';

function App() {
  // Check and clear invalid tokens on app load
  useEffect(() => {
    checkAndClearInvalidAuth();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              {/* Public routes */}
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />
              <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
              <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Outlet />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
                <Route path={ROUTES.USERS} element={<UsersPage />} />
                <Route path="/users/:id" element={<UserDetailPage />} />
                <Route path={ROUTES.OAUTH_CLIENTS} element={<OAuthClientsPage />} />
                <Route path={ROUTES.ROLES} element={<RolesPage />} />
                <Route path={ROUTES.TENANTS} element={<TenantsPage />} />
                <Route path={ROUTES.POLICIES} element={<PoliciesPage />} />
                <Route path={ROUTES.ANALYTICS} element={<AnalyticsPage />} />
                <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
              </Route>

              {/* Catch all - redirect to dashboard */}
              <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
